/**
 * Field Sales REST client and types.
 *
 * Its own module for the reason `sales-api.ts` gives: one import per product
 * keeps it obvious which surface a page talks to. Everything here is the
 * signed-in person's own -- no call takes a user id.
 */

import { request } from "./api";
import type { SalesMeetingMode } from "./sales-types";

export interface FieldMeetingBrief {
  id: number;
  subject: string | null;
  companyName: string;
  startAt: string;
  endAt: string | null;
  mode: SalesMeetingMode;
  internal: boolean;
}

export interface FieldTask {
  id: string;
  title: string;
  board: string;
  dueAt: string | null;
  overdue: boolean;
  minutesLate: number;
  url: string;
}

export interface FieldHome {
  meetings: {
    week: { count: number; next: FieldMeetingBrief | null };
    today: FieldMeetingBrief[];
    needsNotes: number;
    needsFollowUp: number;
  };
  note: { day: string; lines: string[]; updatedAt: string | null };
  tasks: {
    enabled: boolean;
    open: number;
    overdue: number;
    next: FieldTask | null;
    unreadable: number;
    error: string | null;
  };
}

export interface FieldTaskList {
  enabled: boolean;
  tasks: FieldTask[];
  unreadable: number;
  error: string | null;
}

/** Where a page sits in the list: today, the last week, the last month, or "YYYY-MM". */
export type QuickNoteGroup = "today" | "week" | "month" | string;

export interface QuickNoteRow {
  day: string;
  preview: string;
  /** The words around a search hit, when searching. */
  match: string | null;
  updatedAt: string;
  edited: boolean;
  group: QuickNoteGroup;
}

export interface QuickNoteTask {
  text: string;
  taskId: string;
  url: string;
  companyId: number;
  assigneeNames: string[];
  raisedAt: string;
}

export interface QuickNote {
  day: string;
  html: string;
  updatedAt: string | null;
  edited: boolean;
  tasks: QuickNoteTask[];
}

const send = (body: unknown, method = "POST"): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

export const fieldApi = {
  home: () => request<FieldHome>("/field/home"),

  tasks: () => request<FieldTaskList>("/field/tasks"),

  notes: (q?: string) =>
    request<{ today: string; notes: QuickNoteRow[] }>(
      `/field/notes${q?.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`,
    ),

  note: (day: string) => request<QuickNote>(`/field/notes/${day}`),

  saveNote: (day: string, html: string) =>
    request<{ day: string; updatedAt: string | null; edited: boolean; saved: boolean }>(
      `/field/notes/${day}`,
      send({ html }, "PUT"),
    ),

  raiseTask: (
    day: string,
    input: { text: string; companyId: number; assigneeIds?: string[]; dueAt?: string | null },
  ) => request<{ tasks: QuickNoteTask[] }>(`/field/notes/${day}/tasks`, send(input)),
};

/**
 * Kept under the `sales` root, as the diary's keys are, so anything that
 * invalidates the diary -- notes written, a follow-up sent -- refreshes Home's
 * cards in the same step.
 */
export const fieldKeys = {
  home: ["sales", "field-home"] as const,
  tasks: ["sales", "field-tasks"] as const,
  notes: (q: string) => ["field", "notes", q] as const,
  notesRoot: ["field", "notes"] as const,
  note: (day: string) => ["field", "note", day] as const,
};

/**
 * Today's date in the UK, "YYYY-MM-DD".
 *
 * The notepad turns over at midnight London time wherever the phone thinks it
 * is, so this asks for London explicitly rather than reading the local clock.
 */
export function ukToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** "Mon 12 Oct" -- how a page is titled. With `long`, "Monday 12 October 2026". */
export function dayTitle(day: string, long = false): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-GB", {
    weekday: long ? "long" : "short",
    day: "numeric",
    month: long ? "long" : "short",
    ...(long ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}
